import {
	Button,
	DialogTrigger,
	Heading,
	Popover,
	Text,
	TextField,
} from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { EMAIL_REGEX } from '@/utils/contactsCsv';

const headingStyles = style({
	font: 'title',
	color: 'heading',
	fontSize: 'title-2xl',
	fontWeight: 'bold',
});

interface TestSendEmailPopoverProps {
	instanceId: string;
	subject: string;
	isDisabled?: boolean;
}

type SendEmailResult = {
	status: string;
};

export function TestSendEmailPopover({
	instanceId,
	subject,
	isDisabled = false,
}: TestSendEmailPopoverProps) {
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();

	const [isOpen, setIsOpen] = useState(false);
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState<string | null>(null);
	const [status, setStatus] = useState<'idle' | 'pending' | 'sent' | 'error'>(
		'idle',
	);
	const [sendError, setSendError] = useState<string | null>(null);

	const close = useCallback(() => setIsOpen(false), []);

	const handleOpenChange = useCallback((open: boolean) => {
		setIsOpen(open);
		if (!open) {
			setEmail('');
			setEmailError(null);
			setStatus('idle');
			setSendError(null);
		}
	}, []);

	const handleSend = useCallback(async () => {
		const trimmed = email.trim();
		if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
			setEmailError('Please enter a valid email address');
			return;
		}
		if (!userId) {
			setSendError('Unable to send: missing user ID.');
			return;
		}

		setEmailError(null);
		setSendError(null);
		setStatus('pending');

		try {
			await bridgewayApi.post<SendEmailResult>(
				`/api/user/${userId}/email/instance/${instanceId}/send`,
				{
					to_addresses: [trimmed],
					subject,
					should_send_test_email: true,
				},
			);
			setStatus('sent');
			setTimeout(close, 1500);
		} catch (err) {
			setStatus('error');
			setSendError(
				err instanceof Error ? err.message : 'Failed to send test email.',
			);
		}
	}, [bridgewayApi, close, email, instanceId, subject, userId]);

	return (
		<DialogTrigger>
			<Button
				type="button"
				variant="secondary"
				isDisabled={isDisabled || !userId}
				onPress={() => setIsOpen(true)}
			>
				Send Test Email
			</Button>
			<Popover
				placement="top"
				hideArrow
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
			>
				<div
					className="flex flex-col gap-6 p-6"
					style={{ minWidth: '320px', maxWidth: '480px' }}
				>
					<div className="flex flex-col gap-2">
						<Heading styles={headingStyles}>Send a test email</Heading>
						<Text>
							Your email will be sent with [TEST] prepended to the subject.
						</Text>
					</div>
					<div className="flex flex-col gap-3">
						<TextField
							label="Recipient email"
							value={email}
							onChange={(v) => {
								setEmail(v);
								if (emailError) setEmailError(null);
							}}
							type="email"
							placeholder="you@example.com"
							isInvalid={!!emailError}
							errorMessage={emailError ?? undefined}
							isDisabled={status === 'pending'}
						/>
						<div className="flex items-center justify-between">
							<div className="min-h-5 text-sm">
								{sendError && <span className="text-red-600">{sendError}</span>}
								{status === 'sent' && (
									<span className="text-green-600">Test email sent!</span>
								)}
							</div>
							<Button
								variant="accent"
								onPress={() => void handleSend()}
								isPending={status === 'pending'}
								isDisabled={!email.trim() || status === 'pending'}
							>
								Send
							</Button>
						</div>
					</div>
				</div>
			</Popover>
		</DialogTrigger>
	);
}
